/**
 * Base class for all size calculator related errors
 */
export abstract class SizeCalculatorError extends Error {
  public readonly code: string;
  public readonly context?: Record<string, unknown>;

  constructor(message: string, code: string, context?: Record<string, unknown>) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.context = context;

    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown when no boxes are provided to the calculator
 */
export class EmptyBoxListError extends SizeCalculatorError {
  constructor() {
    super('최소 하나 이상의 box가 필요합니다.', 'EMPTY_BOX_LIST');
  }
}

/**
 * Error thrown when the provided margin is too large
 */
export class ExcessiveMarginError extends SizeCalculatorError {
  constructor(margin: number, minSideWithMargin: number) {
    const context = { margin, minSideWithMargin };
    super(
      `제공된 마진 ${margin}mm 이 너무 큽니다. 마진의 크기는 최소 요구조건인 ${minSideWithMargin}mm 의 절반보다 작아야 합니다.`,
      'EXCESSIVE_MARGIN',
      context,
    );
  }
}

/**
 * Error thrown when no suitable boxes can fit the item even with minimum requirements
 */
export class NoViableBoxError extends SizeCalculatorError {
  constructor(margin: number, minSideWithMargin: number, requiredMinItemSide: number) {
    const context = {
      margin,
      minSideWithMargin,
      requiredMinItemSide: Number(requiredMinItemSide.toFixed(1)),
    };
    super(
      `모든 가능한 Bounding Box를 고려해도 최소 크기 조건을 만족할 수 없습니다. ` +
        `최소 요구사항: 마진 ${margin}mm 을 포함한 최소 크기 ${minSideWithMargin}mm, ` +
        `실제 필요한 아이템 크기: ${requiredMinItemSide.toFixed(1)}mm 이상`,
      'NO_VIABLE_BOX',
      context,
    );
  }
}

/**
 * Error thrown when no box can fit the item at minimum required scale
 */
export class MinScaleFitError extends SizeCalculatorError {
  constructor(minSideWithMargin: number, margin: number, minScaleRequired: number) {
    const context = {
      minSideWithMargin,
      margin,
      minScaleRequired: Number(minScaleRequired.toFixed(3)),
    };
    super(
      `최소 크기 조건 ${minSideWithMargin}mm (margin ${margin}mm 포함)을 만족하는 스케일(${minScaleRequired.toFixed(3)})이 적용되었을 때 ` +
        `어떤 박스에도 들어갈 수 없습니다.`,
      'MIN_SCALE_FIT_ERROR',
      context,
    );
  }
}

/**
 * Error thrown when getCurrentSize is called before getMinMaxItemSize
 */
export class NormalizedItemSizeNotInitializedError extends SizeCalculatorError {
  constructor() {
    super('getMinMaxItemSize must be called before getCurrentSize.', 'NORMALIZED_ITEM_SIZE_NOT_INITIALIZED');
  }
}

export class MinMaxScaleInvalidError extends SizeCalculatorError {
  constructor(minScale: number, maxScale: number) {
    super(
      `minScale(${minScale})과 maxScale(${maxScale})의 관계가 잘못되었습니다. minScale은 maxScale보다 반드시 작아야 합니다.`,
      'MIN_MAX_SCALE_INVALID',
      { minScale, maxScale },
    );
  }
}
